import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Typography,
  Box,
} from "@mui/material";
import { CancelButton, CreateRvcButton, CustomDialogActions } from "./style";

interface CreateRevenueCenterDialogProps {
  open: boolean;
  onClose: () => void;
  propertyData: Property;
  storeData: Store;
  onSubmit: (rvcData: {
    id: string;
    rvc_name: string;
    property_id: string;
    store_id: string;
    rvc_short_name: string;
    location: string;
  }) => void;
}

const CreateRevenueCenterDialog: React.FC<CreateRevenueCenterDialogProps> = ({
  open,
  onClose,
  propertyData,
  storeData,
  onSubmit,
}) => {
  const [formData, setFormData] = useState({
    id: "",
    rvc_name: "",
    rvc_short_name: "",
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const handleInputChange =
    (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));

      // Clear error when user starts typing
      if (errors[field]) {
        setErrors((prev) => ({
          ...prev,
          [field]: "",
        }));
      }
    };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.id.trim()) {
      newErrors.id = "RVC ID is required";
    }

    if (!formData.rvc_name.trim()) {
      newErrors.rvc_name = "RVC name is required";
    }

    if (!formData.rvc_short_name.trim()) {
      newErrors.rvc_short_name = "RVC short name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit({
        ...formData,
        property_id: propertyData.id,
        store_id: storeData.id,
        location: propertyData.id,
      });
    }
  };

  const handleClose = () => {
    setFormData({
      id: "",
      rvc_name: "",
      rvc_short_name: "",
    });
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Revenue Center</DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="textSecondary">
            <strong>Property:</strong> {propertyData.property_name} (
            {propertyData.short_name})
          </Typography>
          <Typography variant="body2" color="textSecondary">
            <strong>Store:</strong> {storeData.store_name}{" "}
            {storeData.short_name && `(${storeData.short_name})`}
          </Typography>
        </Box>

        <TextField
          fullWidth
          label="RVC ID"
          value={formData.id}
          onChange={handleInputChange("id")}
          error={!!errors.id}
          helperText={errors.id}
          inputProps={{ maxLength: 100 }}
          margin="normal"
          required
        />

        <TextField
          fullWidth
          label="RVC Name"
          value={formData.rvc_name}
          onChange={handleInputChange("rvc_name")}
          error={!!errors.rvc_name}
          helperText={errors.rvc_name}
          inputProps={{ maxLength: 100 }}
          margin="normal"
          required
        />

        <TextField
          fullWidth
          label="RVC Short Name"
          value={formData.rvc_short_name}
          onChange={handleInputChange("rvc_short_name")}
          error={!!errors.rvc_short_name}
          helperText={errors.rvc_short_name}
          inputProps={{ maxLength: 100 }}
          margin="normal"
          required
        />
      </DialogContent>
      <CustomDialogActions>
        <CancelButton onClick={handleClose}>Cancel</CancelButton>
        <CreateRvcButton
          onClick={handleSubmit}
          variant="contained"
          color="primary"
        >
          Create Revenue Center
        </CreateRvcButton>
      </CustomDialogActions>
    </Dialog>
  );
};

export default CreateRevenueCenterDialog;
